import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Equal, Lock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';
import {
  editarInventario,
  type InventarioRow,
  type StockBuckets,
  type VentaHolds,
} from '../api';

/**
 * Admin override for the 7 stock buckets of an Inventario row. Mirrors
 * PowerApps's PopUp_EditarInventoryItem (Screen_Inventario.pa.yaml:2108+).
 * Every mutation writes a `CodigoStatus_BAA='EM'` audit row on the
 * server with the bucket diff, so the historial tab surfaces the change
 * (PowerApps doesn't write a BAA for manual edits — this is a strict
 * audit-trail upgrade).
 *
 * Total recomputes live as the admin edits any bucket.
 *
 * Los tres buckets de ventas (Pago Pendiente, Pago Parcial, Pendiente
 * Despacho) son de sólo lectura: los mueven las ventas. Si no coinciden con
 * lo que retienen las ventas abiertas, se pueden *igualar* a ese número —
 * es lo único que acepta el servidor (`findLockedBucketEdit`).
 */

const BUCKETS: Array<{
  key: keyof StockBuckets;
  label: string;
  hint?: string;
  /** Bucket que mueven las ventas: sólo se puede igualar a las ventas abiertas. */
  venta?: boolean;
}> = [
  { key: 'disponible', label: 'Disponible', hint: 'Listo para vender' },
  { key: 'reservado', label: 'Pago Pendiente', hint: 'Pago cargado, sin confirmar', venta: true },
  { key: 'reservadoParcial', label: 'Pago Parcial', hint: 'Pago parcial confirmado', venta: true },
  { key: 'pendienteDespacho', label: 'Pendiente Despacho', hint: 'Pagado, a despachar', venta: true },
  { key: 'porIngresar', label: 'Por Ingresar', hint: 'En recepción' },
  { key: 'prestShow', label: 'Prest/Show', hint: 'Préstamo o showroom' },
];

export function EditarInventarioDialog({
  row,
  holds,
  onClose,
}: {
  row: InventarioRow;
  /**
   * Unidades que las ventas abiertas tienen tomadas por bucket: el único
   * valor (además del actual) al que se puede llevar un bucket de ventas.
   * El servidor lo vuelve a chequear al guardar (ahí es donde manda).
   */
  holds: VentaHolds;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<StockBuckets>(() => ({ ...row.buckets }));

  const total = useMemo(
    () =>
      draft.disponible +
      draft.cotizado +
      draft.reservado +
      draft.reservadoParcial +
      draft.pendienteDespacho +
      draft.porIngresar +
      draft.prestShow,
    [draft],
  );

  const dirty = useMemo(() => {
    return (Object.keys(draft) as Array<keyof StockBuckets>).some(
      (k) => Math.abs(draft[k] - row.buckets[k]) > 0.0001,
    );
  }, [draft, row.buckets]);

  // Unidades que igualar los buckets de ventas agrega (+) o saca (−) del total.
  const deltaVentas = useMemo(
    () =>
      BUCKETS.filter((b) => b.venta).reduce(
        (acc, b) => acc + (draft[b.key] - row.buckets[b.key]),
        0,
      ),
    [draft, row.buckets],
  );
  // Disponible con la diferencia compensada: lo que sale de los buckets de
  // ventas vuelve a Disponible, lo que entra sale de Disponible.
  const disponibleCompensado = Math.max(0, row.buckets.disponible - deltaVentas);
  const compensado = Math.abs(draft.disponible - disponibleCompensado) < 0.0001;

  // Cambio de físico (misma cuenta que el server): crea o da de baja unidades.
  const unidades = useMemo(() => efectoEnUnidades(row.buckets, draft), [row.buckets, draft]);
  const cambiaFisico = unidades.altas > 0 || unidades.bajas > 0 || unidades.traslados > 0;
  const [motivo, setMotivo] = useState('');
  const faltaMotivo = cambiaFisico && motivo.trim().length === 0;

  const canSubmit = dirty && !faltaMotivo;

  const mutation = useMutation({
    mutationFn: () =>
      editarInventario(row.id, {
        buckets: draft,
        expectedBuckets: row.buckets,
        ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
      }),
    onSuccess: (res) => {
      // El formulario no deja armar un ajuste inválido, así que un rechazo
      // del servidor significa que el stock o las ventas cambiaron mientras
      // estaba abierto: se cierra y se recargan los valores en vez de dejar
      // en pantalla un borrador armado sobre datos viejos.
      if (res.outcome !== 'patched') {
        toast.error(res.message, { duration: 12000 });
      } else {
        toast.success('Ajuste aplicado');
      }
      qc.invalidateQueries({ queryKey: ['inventario'] });
      onClose();
    },
    onError: () => {
      toast.error('No se pudo aplicar el ajuste');
    },
  });

  const setBucket = (key: keyof StockBuckets, raw: string) => {
    const parsed = raw.trim() === '' ? 0 : Number(raw.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed < 0) return;
    setDraft((prev) => ({ ...prev, [key]: parsed }));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-2xl"
        processing={mutation.isPending && 'Aplicando ajuste…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" aria-hidden />
            Editar {row.fields.Articulo_I || 'artículo'}
          </DialogTitle>
          <DialogDescription>
            Ajuste manual de stock. El cambio queda registrado en el historial. Los
            buckets de ventas los mueven las ventas: acá sólo se pueden igualar a lo
            que tienen las ventas abiertas.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {BUCKETS.map((b) => {
              const changed = Math.abs(draft[b.key] - row.buckets[b.key]) > 0.0001;
              const hold = b.venta ? holds[b.key as keyof VentaHolds] : undefined;
              const descuadrado =
                hold != null && Math.abs(row.buckets[b.key] - hold.unidades) > 0.0001;
              return (
                <div key={b.key} className="space-y-1">
                  <Label htmlFor={`bucket-${b.key}`} className="text-sm">
                    {b.label}
                    {b.hint && (
                      <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                        · {b.hint}
                      </span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input
                        id={`bucket-${b.key}`}
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="0.01"
                        value={String(draft[b.key])}
                        onChange={(e) => setBucket(b.key, e.target.value)}
                        readOnly={b.venta}
                        tabIndex={b.venta ? -1 : undefined}
                        title={b.venta ? 'Lo mueven las ventas' : undefined}
                        className={cn(
                          'tabular-nums',
                          b.venta && 'cursor-default bg-muted/40 pr-8 text-muted-foreground',
                          changed && 'border-amber-400 bg-amber-50 text-foreground',
                        )}
                      />
                      {b.venta && (
                        <Lock
                          className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                          aria-hidden
                        />
                      )}
                    </div>
                    {changed && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        antes {row.buckets[b.key]}
                      </span>
                    )}
                    {hold && descuadrado && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 shrink-0 gap-1.5"
                        onClick={() =>
                          setDraft((prev) => ({
                            ...prev,
                            [b.key]: changed ? row.buckets[b.key] : hold.unidades,
                          }))
                        }
                      >
                        {changed ? (
                          'Deshacer'
                        ) : (
                          <>
                            <Equal className="h-3.5 w-3.5" aria-hidden />
                            Igualar a {hold.unidades}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  {hold && (
                    <p
                      className={cn(
                        'text-[11px]',
                        descuadrado && !changed
                          ? 'font-medium text-amber-700'
                          : 'text-muted-foreground',
                      )}
                    >
                      {hold.unidades > 0
                        ? `Ventas abiertas: ${hold.unidades} (${hold.ventas.join(', ')})`
                        : 'Ninguna venta abierta en este estado'}
                      {descuadrado && !changed && ' — no coincide'}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-baseline justify-between rounded-lg border border-foreground/20 bg-muted/30 px-3 py-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Total Stock
            </span>
            <span className="text-lg font-semibold tabular-nums">
              {formatNumber(total)}
              <span className="ml-2 text-xs text-muted-foreground">
                (antes {row.totalStock})
              </span>
            </span>
          </div>

          {(unidades.altas > 0 || unidades.bajas > 0) && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <div className="space-y-1">
                {unidades.altas > 0 && (
                  <p>
                    Se crean <strong>{formatNumber(unidades.altas)} unidades sin origen</strong> (sin
                    compra, importación ni costo).
                    {row.buckets.porIngresar > 0
                      ? ` Si llegó mercadería de las ${formatNumber(row.buckets.porIngresar)} Por Ingresar, cargala desde Recepción: así queda con su compra, su importación y su serie.`
                      : ' Si llegó mercadería de una compra, cargala desde Recepción.'}
                  </p>
                )}
                {unidades.bajas > 0 && (
                  <p>
                    Se dan de baja <strong>{formatNumber(unidades.bajas)} unidades</strong>, las más
                    viejas primero.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="ajuste-motivo" className="text-sm">
              Motivo
              <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                {cambiaFisico ? '· obligatorio: cambia el stock físico' : '· opcional'}
              </span>
            </Label>
            <Input
              id="ajuste-motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={300}
              placeholder="Ej.: recuento del depósito del 04/11"
              aria-invalid={faltaMotivo || undefined}
              className={cn(faltaMotivo && 'border-amber-400')}
            />
            <p className="text-[11px] text-muted-foreground">Queda en el historial y en las unidades.</p>
          </div>

          {Math.abs(deltaVentas) > 0.0001 && (
            <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 sm:flex-row sm:items-center">
              <p className="flex-1">
                {deltaVentas < 0
                  ? `Igualar saca del total ${formatNumber(-deltaVentas)} un. que no son de ninguna venta. Si están en el depósito, pasalas a Disponible.`
                  : `Igualar suma ${formatNumber(deltaVentas)} un. que reclaman las ventas. Si ya estaban contadas en Disponible, descontalas de ahí.`}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 bg-background"
                disabled={compensado}
                onClick={() =>
                  setDraft((prev) => ({ ...prev, disponible: disponibleCompensado }))
                }
              >
                {deltaVentas < 0 ? 'Pasar a Disponible' : 'Descontar de Disponible'}
              </Button>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            title={faltaMotivo ? 'Indicá el motivo del ajuste' : undefined}
            className="gap-2"
          >
            <Pencil className="h-4 w-4" aria-hidden />
            Aplicar ajuste
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Qué hace el ajuste con las unidades (misma regla que `planAjusteFisico` en
 * el server): lo que pasa entre depósito y Prest/Show se traslada; el resto
 * crea unidades sin origen o da de baja las más viejas. Por Ingresar y
 * Cotizado no son físicos.
 */
function efectoEnUnidades(
  antes: StockBuckets,
  despues: StockBuckets,
): { altas: number; bajas: number; traslados: number } {
  const pos = (n: number) => Math.max(0, n);
  const deposito = (b: StockBuckets) =>
    pos(b.disponible) + pos(b.reservado) + pos(b.reservadoParcial) + pos(b.pendienteDespacho);
  let dep = Math.round((deposito(despues) - deposito(antes)) * 1000) / 1000;
  let pre = Math.round((pos(despues.prestShow) - pos(antes.prestShow)) * 1000) / 1000;
  let traslados = 0;
  if ((dep < 0 && pre > 0) || (dep > 0 && pre < 0)) {
    traslados = Math.min(Math.abs(dep), Math.abs(pre));
    dep += dep < 0 ? traslados : -traslados;
    pre += pre < 0 ? traslados : -traslados;
  }
  return {
    altas: pos(dep) + pos(pre),
    bajas: pos(-dep) + pos(-pre),
    traslados,
  };
}

function formatNumber(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}
